/**
 * Custom hooks for Frontegg auth integration.
 * 
 * Replaces the old `superUser` prop threading pattern with direct hook calls.
 * 
 * Usage:
 *   const isAdmin = useIsAdmin();
 *   const canEdit = useCanEdit();
 *   const { isEntitled } = useHasFeature('cms-publish');
 */

import { useAuth, useFeatureEntitlements, useAuthUserOrNull } from '@frontegg/react';
import { evaluateFeatureFlag, prepareAttributes } from '@frontegg/entitlements-javascript-commons';
import { useEffect, useRef, useState, useMemo } from 'react';

const _isFixtureMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('designFixture') === '1';

// Role constants — must match Frontegg role keys
export const ROLES = {
  ADMIN: 'Admin',
  EDITOR: 'AlchemiqEditor',
  REGULAR: 'RegularUser',
  READONLY: 'ReadonlyUser',
};

const ROLE_LEVELS = {
  [ROLES.ADMIN]: 100,
  [ROLES.EDITOR]: 50,
  [ROLES.REGULAR]: 20,
  [ROLES.READONLY]: 10,
};

function getRoleKey(r) {
  return typeof r === 'string' ? r : (r.key || r.name);
}

/**
 * Get the highest role key from a roles array.
 * @param {Array} roles
 * @returns {string|null}
 */
function getHighestRole(roles) {
  let highest = null;
  let highestLevel = -1;
  for (const r of roles) {
    const key = getRoleKey(r);
    const level = ROLE_LEVELS[key] || 0;
    if (level > highestLevel) { highestLevel = level; highest = key; }
  }
  return highest;
}

/**
 * Check if the current user has the 'Admin' role (replaces superUser checks).
 * @returns {boolean}
 */
export function useIsAdmin() {
  const user = useAuthUserOrNull();
  
  if (!user || !user.roles) return false;
  
  return user.roles.some(
    r => getRoleKey(r) === ROLES.ADMIN
  );
}

/**
 * Check if the current user can edit (Admin or AlchemiqEditor).
 * @returns {boolean}
 */
export function useCanEdit() {
  const user = useAuthUserOrNull();
  if (!user || !user.roles) return false;
  return user.roles.some(r => {
    const key = getRoleKey(r);
    return key === ROLES.ADMIN || key === ROLES.EDITOR;
  });
}

/**
 * Check if the current user can create briefs (Admin, Editor, or RegularUser — everyone except ReadonlyUser).
 * @returns {boolean}
 */
export function useCanCreateBrief() {
  const user = useAuthUserOrNull();
  if (!user || !user.roles) return false;
  return user.roles.some(r => {
    const level = ROLE_LEVELS[getRoleKey(r)] || 0;
    return level >= ROLE_LEVELS[ROLES.REGULAR];
  });
}

/**
 * Check if the current user has at least the specified role level.
 * @param {string} minRoleKey - The minimum role key required
 * @returns {boolean}
 */
export function useHasMinRole(minRoleKey) {
  const user = useAuthUserOrNull();
  if (!user || !user.roles) return false;
  const minLevel = ROLE_LEVELS[minRoleKey] || 0;
  return user.roles.some(r => (ROLE_LEVELS[getRoleKey(r)] || 0) >= minLevel);
}

/**
 * Check if the current user is entitled to a specific feature.
 * Uses Frontegg Entitlements system for feature flag gating.
 *
 * If entitlements data was not loaded during authentication (the SDK silently
 * swallows load errors), this hook manually fetches the v2 user-entitlements
 * endpoint once and caches the result. This ensures feature flags work even
 * when the SDK's internal noThrowLoadEntitlements() fails.
 * 
 * @param {string} featureKey - The feature key to check (e.g., 'social-posts-creation')
 * @returns {{ isEntitled: boolean, isLoading: boolean }}
 */

// Module-level cache for manually fetched entitlements
let _manualEntitlements = null;
let _manualEntitlementsFetching = false;
let _manualEntitlementsListeners = [];

function notifyManualEntitlementsListeners() {
  _manualEntitlementsListeners.forEach(fn => fn());
  _manualEntitlementsListeners = [];
}

export function useHasFeature(featureKey) {
  if (_isFixtureMode) {
    return { isEntitled: false, isLoading: false };
  }
  return _useHasFeatureImpl(featureKey);
}

function _useHasFeatureImpl(featureKey) {
  const user = useAuthUserOrNull();
  const [, forceUpdate] = useState(0);
  const attemptedRef = useRef(false);

  // Build custom attributes with the user's role for feature flag targeting.
  // This allows Frontegg feature flags to target by "role" custom attribute.
  const userRoles = user?.roles || [];
  const highestRole = getHighestRole(userRoles);
  const roleNames = userRoles.map(r => getRoleKey(r));
  const customAttributes = {
    role: highestRole || '',
    roles: roleNames.join(','),
  };

  // Always call at top level (React hooks rules — no try/catch wrapping)
  const sdkResult = useFeatureEntitlements(featureKey, customAttributes);

  // Check if SDK entitlements are actually loaded (features object exists and is non-empty)
  const hasEntitlements = user?.entitlements?.features 
    && typeof user.entitlements.features === 'object'
    && Object.keys(user.entitlements.features).length > 0;

  // Always fetch manual entitlements to get full feature flag data for targeting evaluation.
  // The SDK may load entitlements but its evaluation chain includes "direct entitlement"
  // which bypasses feature flag targeting rules. We need the raw data to evaluate flags ourselves.
  useEffect(() => {
    if (!user?.accessToken) return; // No JWT yet
    if (attemptedRef.current) return; // Already tried
    if (_manualEntitlements) return; // Already fetched by another hook instance

    attemptedRef.current = true;

    // If another instance is already fetching, wait for it
    if (_manualEntitlementsFetching) {
      _manualEntitlementsListeners.push(() => forceUpdate(v => v + 1));
      return;
    }

    _manualEntitlementsFetching = true;

    const baseUrl = process.env.REACT_APP_FRONTEGG_BASE_URL;
    const appId = process.env.REACT_APP_FRONTEGG_APP_ID;

    if (!baseUrl) {
      console.warn('[useHasFeature] REACT_APP_FRONTEGG_BASE_URL not set, cannot fetch entitlements');
      _manualEntitlementsFetching = false;
      return;
    }

    const headers = {
      'Authorization': `Bearer ${user.accessToken}`,
    };
    if (appId) {
      headers['frontegg-requested-application-id'] = appId;
    }

    console.info('[useHasFeature] Fetching entitlements from', baseUrl);

    fetch(`${baseUrl}/entitlements/api/v2/user-entitlements`, { headers })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        console.info('[useHasFeature] Manual entitlements loaded:', 
          data.features ? Object.keys(data.features) : 'no features');
        _manualEntitlements = data;
        _manualEntitlementsFetching = false;
        forceUpdate(v => v + 1);
        notifyManualEntitlementsListeners();
      })
      .catch(err => {
        console.warn('[useHasFeature] Manual entitlements fetch failed:', err.message);
        _manualEntitlementsFetching = false;
        notifyManualEntitlementsListeners();
      });
  }, [user?.accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Evaluate entitlement ---
  // IMPORTANT: Frontegg's default evaluation chain runs:
  //   1. Direct entitlement (feature in plan → plan on tenant) — grants ALL users
  //   2. Feature flag targeting rules
  // The chain stops at the first positive result. So if a feature is in a plan
  // assigned to the tenant, EVERY user is entitled and the feature flag rules
  // are never evaluated. We override this: when a feature has a feature flag,
  // we evaluate ONLY the flag targeting rules so you can control per-role access.

  // Try to get feature data from manual fetch or SDK
  const manualFeature = _manualEntitlements?.features?.[featureKey];
  const sdkFeature = user?.entitlements?.features?.[featureKey];
  const featureData = manualFeature || sdkFeature;

  // If we have feature data with a feature flag, evaluate ONLY the flag targeting
  if (featureData?.featureFlag) {
    const prepared = prepareAttributes({ custom: customAttributes });
    const { treatment } = evaluateFeatureFlag(featureData.featureFlag, prepared);
    const entitled = treatment === 'true';
    console.debug(`[useHasFeature] Feature flag evaluation for "${featureKey}": treatment=${treatment}, role=${highestRole}`);
    return { isEntitled: entitled, isLoading: false };
  }

  // No feature flag — feature is controlled purely by plan assignment (direct entitlement).
  // If the feature exists in entitlements data at all, the user is entitled.
  if (featureData) {
    console.debug(`[useHasFeature] Direct entitlement for "${featureKey}" (no feature flag)`);
    return { isEntitled: true, isLoading: false };
  }

  // If we have entitlements data but feature is missing, user is not entitled
  if (hasEntitlements || _manualEntitlements) {
    console.debug(`[useHasFeature] Feature "${featureKey}" not found in entitlements`);
    return { isEntitled: false, isLoading: false };
  }

  // If manual fetch is in progress, indicate loading
  if (_manualEntitlementsFetching) {
    return { isEntitled: false, isLoading: true };
  }

  // Fallback: not entitled
  return { isEntitled: false, isLoading: false };
}

/**
 * Get authentication status + Frontegg user without importing @frontegg/react
 * at the call site (keeps consumers mockable via this module in tests).
 * @returns {{ isAuthenticated: boolean, user: Object|null }}
 */
export function useAuthState() {
  const { isAuthenticated, user } = useAuth();
  return { isAuthenticated: Boolean(isAuthenticated), user: user || null };
}

/**
 * Get the Frontegg access token for API calls.
 * This replaces localStorage.getItem('token').
 * 
 * @returns {string|null} The access token or null if not authenticated
 */
export function useFronteggToken() {
  if (_isFixtureMode) return null;
  return _useFronteggTokenImpl();
}
function _useFronteggTokenImpl() {
  const { user } = useAuth(); // eslint-disable-line react-hooks/rules-of-hooks
  return user?.accessToken || null;
}

/**
 * Get basic user info from Frontegg.
 * @returns {{ email: string, name: string, isAdmin: boolean, canEdit: boolean, roles: string[], highestRole: string|null } | null}
 */
export function useUserInfo() {
  const user = useAuthUserOrNull();
  const admin = useIsAdmin();
  const edit = useCanEdit();
  
  if (!user) return null;
  
  const roles = (user.roles || []).map(r => getRoleKey(r));
  
  return {
    email: user.email,
    name: user.name,
    isAdmin: admin,
    canEdit: edit,
    roles,
    highestRole: getHighestRole(user.roles || []),
    profilePictureUrl: user.profilePictureUrl,
  };
}
